import React, { createContext, useContext, useReducer } from 'react'
import { SnackbarProvider } from 'notistack'
import BaseSnackbar from 'features/notifications/Components/BaseSnackbar'
import { Notification } from './Notifications.type'
import reducer, { actions, initialState } from './Notifications.slice'
import Notifier from 'features/notifications/Notifier'

export interface NotificationsContextValue {
  notifications: Notification[]
  enqueueSnackbar: (...args: Parameters<typeof actions.enqueueSnackbar>) => void
  enqueueSnackbarSuccess: (...args: Parameters<typeof actions.enqueueSnackbarSuccess>) => void
  enqueueSnackbarError: (...args: Parameters<typeof actions.enqueueSnackbarError>) => void
  closeSnackbar: (...args: Parameters<typeof actions.closeSnackbar>) => void
  removeSnackbar: (...args: Parameters<typeof actions.removeSnackbar>) => void
}

export const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined)

export function useNotificationsContext() {
  return useContext(NotificationsContext)!
}

export default function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  function enqueueSnackbar(...args: Parameters<typeof actions.enqueueSnackbar>) {
    dispatch(actions.enqueueSnackbar(...args))
  }

  function enqueueSnackbarSuccess(...args: Parameters<typeof actions.enqueueSnackbarSuccess>) {
    dispatch(actions.enqueueSnackbarSuccess(...args))
  }

  function enqueueSnackbarError(...args: Parameters<typeof actions.enqueueSnackbarError>) {
    dispatch(actions.enqueueSnackbarError(...args))
  }

  function closeSnackbar(...args: Parameters<typeof actions.closeSnackbar>) {
    dispatch(actions.closeSnackbar(...args))
  }

  function removeSnackbar(...args: Parameters<typeof actions.removeSnackbar>) {
    dispatch(actions.removeSnackbar(...args))
  }

  return (
    <NotificationsContext.Provider
      value={{
        notifications: state.items,
        enqueueSnackbar,
        enqueueSnackbarSuccess,
        enqueueSnackbarError,
        closeSnackbar,
        removeSnackbar,
      }}
    >
      <SnackbarProvider
        Components={{
          default: BaseSnackbar,
          info: BaseSnackbar,
          success: BaseSnackbar,
          warning: BaseSnackbar,
          error: BaseSnackbar,
        }}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
        maxSnack={3}
      >
        <Notifier />
        {children}
      </SnackbarProvider>
    </NotificationsContext.Provider>
  )
}
